import React, { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { t } from 'i18next';
import { VpBreadcrumbs, VpBreadcrumbsItem } from '@vtmn-play/react';

import { useFetchIlinkProcesses } from '../../apis/useFetch/ilink-process';
import { interventionNotStarted } from '../../store/selectors/intervention';
import { getBreadcrumbPath, getBreadcrumbFromSportsCatalog } from '../../utils/path/breadcrumbPath';
import { selectCatalogSearch } from '../../store/selectors/catalog';
import { updateCatalogSearch } from '../../store/catalogSlice';
import { getAppRoute, RoutesPath } from '../../core/routes';
import { type BreadcrumbLevel } from '../../store/catalogSlice';
import { LevelType } from '../../apis/ilink.type';
import { usePrompt } from './TempBlocker';
import { Action, type Location } from './History';
import { useSelectFeatureSettings } from '../../apis/startup/startup.selectors';
import { capitalizeString } from '../../business/helpers';

export const initialBreadcrumb = () => ({
  level: LevelType.processes,
  label: t('catalog.breadcrumb.search.root'),
});

interface BreadcrumbProps {
  readonly setUserInputSearch?: (value: string) => void;
  readonly backToCatalog?: boolean;
  readonly className?: string;
}

const getBreadcrumbLabel = (breadcrumbLevel: BreadcrumbLevel, iLinkResult: string | undefined) =>
  breadcrumbLevel.label ?? iLinkResult ?? breadcrumbLevel.id?.toString() ?? breadcrumbLevel.level;

const BreadCrumbItem = ({
  breadcrumbLevel,
  breadcrumbLink,
  isCurrent = false,
}: {
  readonly breadcrumbLevel: BreadcrumbLevel;
  readonly breadcrumbLink: string;
  readonly isCurrent?: boolean;
}) => {
  const ilinkProcessesRequest = useFetchIlinkProcesses(breadcrumbLevel.id?.toString() ?? breadcrumbLevel.label ?? breadcrumbLevel.level);
  useEffect(() => {
    !ilinkProcessesRequest.result && breadcrumbLevel.id && breadcrumbLevel.label === undefined && ilinkProcessesRequest.fetchResult();
  }, [breadcrumbLevel.id]);

  const breadcrumbLabel = getBreadcrumbLabel(breadcrumbLevel, ilinkProcessesRequest.result?.label);

  return (
    <VpBreadcrumbsItem data-testid={`breadcrumb-${breadcrumbLevel.level}`} href={breadcrumbLink} isCurrent={isCurrent}>
      {capitalizeString(breadcrumbLabel)}
    </VpBreadcrumbsItem>
  );
};

export const Breadcrumb = ({ setUserInputSearch, backToCatalog, className }: BreadcrumbProps) => {
  const catalogSearch = useSelector(selectCatalogSearch);
  const dispatch = useDispatch();
  const isInterventionNotStarted = useSelector(interventionNotStarted);
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbSearch = catalogSearch.breadcrumb;
  const isCatalogSportsActive = useSelectFeatureSettings('activationCatalogSports');

  const breadcrumbAction = (breadcrumb: BreadcrumbLevel, preventNavigation = false) => {
    if (catalogSearch.breadcrumb.indexOf(breadcrumb) !== catalogSearch.breadcrumb.length - 1) {
      setUserInputSearch?.('');
      const updatedBreadcrumbSearch = [...breadcrumbSearch].splice(0, breadcrumbSearch.indexOf(breadcrumb) + 1);
      dispatch(updateCatalogSearch({ ...catalogSearch, breadcrumb: updatedBreadcrumbSearch, fetchNeeded: true }));
      if (backToCatalog && !preventNavigation) {
        if (isCatalogSportsActive) {
          navigate(getAppRoute(RoutesPath.newCatalogSportsPage, getBreadcrumbFromSportsCatalog(breadcrumb, breadcrumbSearch)));
        } else {
          navigate(getAppRoute(RoutesPath.catalogPage, getBreadcrumbPath(breadcrumb, breadcrumbSearch)));
        }
      }
    }
  };

  const breadcrumbLink = (breadcrumb: BreadcrumbLevel, preventNavigation = false): string => {
    if (catalogSearch.breadcrumb.indexOf(breadcrumb) !== catalogSearch.breadcrumb.length - 1) {
      if (backToCatalog && !preventNavigation) {
        if (isCatalogSportsActive) {
          return getAppRoute(RoutesPath.newCatalogSportsPage, getBreadcrumbFromSportsCatalog(breadcrumb, breadcrumbSearch));
        }
        return getAppRoute(RoutesPath.catalogPage, getBreadcrumbPath(breadcrumb, breadcrumbSearch));
      }
    }
    return '';
  };

  const displayBreadcrumb = useCallback(
    (breadcrumbLevel: BreadcrumbLevel) =>
      !breadcrumbLevel.hide &&
      (breadcrumbLevel.level !== LevelType.processes || (breadcrumbLevel.level === LevelType.processes && isInterventionNotStarted)),
    []
  );

  usePrompt((nextLocation: Location, action: Action) => {
    if (
      (nextLocation.pathname.startsWith(getAppRoute(RoutesPath.productPage)) || nextLocation.pathname === getAppRoute(RoutesPath.interventionPage)) &&
      action !== Action.Pop
    ) {
      return true;
    }

    if (location.pathname.startsWith(getAppRoute(RoutesPath.productPage)) && nextLocation.pathname.startsWith(getAppRoute(RoutesPath.catalogPage))) {
      catalogSearch.breadcrumb[catalogSearch.breadcrumb.length - 1].level === LevelType.product &&
        action === Action.Pop &&
        breadcrumbAction(catalogSearch.breadcrumb[catalogSearch.breadcrumb.length - 2], true);
      return true;
    }

    if (
      location.pathname.startsWith('/catalog') &&
      catalogSearch.breadcrumb.length > 1 &&
      action === Action.Pop &&
      displayBreadcrumb(catalogSearch.breadcrumb[catalogSearch.breadcrumb.length - 2])
    ) {
      breadcrumbAction(catalogSearch.breadcrumb[catalogSearch.breadcrumb.length - 2]);
      return false;
    }

    return true;
  }, false);

  return (
    <VpBreadcrumbs className={className} data-testid="breadcrumb">
      {!!catalogSearch.breadcrumb?.length &&
        catalogSearch.breadcrumb.map(
          (breadcrumbLevel, index) =>
            displayBreadcrumb(breadcrumbLevel) && (
              <BreadCrumbItem
                breadcrumbLink={breadcrumbLink(breadcrumbLevel)}
                breadcrumbLevel={breadcrumbLevel}
                key={breadcrumbLevel.level}
                isCurrent={index === catalogSearch.breadcrumb.length - 1}
              />
            )
        )}
    </VpBreadcrumbs>
  );
};
